import {gql, useMutation, useQuery} from '@apollo/client';
import {Box, Button, Typography} from '@mui/material';
import {captureException} from '@sentry/react';
import {useSnackbar} from 'notistack';
import React, {
  PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import {useNavigate} from 'react-router-dom';
import {GET_ACCOUNT_FULL} from '../../components/AccountOverview/accountOverviewQueries';
import {ACCOUNT_DETAILS_FRAGMENT, CONTACT_SEARCH_FRAGMENT} from '../../graphql/fragments';
import withAutoFormNew, {RecursivePartial} from '../../components/AutoForm/AutoFormNew';
import {ConvertLead, ConvertLeadVariables} from '../../schema-types/ConvertLead';
import {CreateLead, CreateLeadVariables} from '../../schema-types/CreateLead';
import {
  CreateLeadAddress,
  CreateLeadAddressVariables,
} from '../../schema-types/CreateLeadAddress';
import {
  SubmitNewContact,
  SubmitNewContactVariables,
} from '../../schema-types/SubmitNewContact';
import {UpdateLead, UpdateLeadVariables} from '../../schema-types/UpdateLead';
import {
  UpdateLeadAddress,
  UpdateLeadAddressVariables,
} from '../../schema-types/UpdateLeadAddress';
import {
  createLeadAddressMutation,
  createLeadMutation,
  updateLeadAddressMutation,
  updateLeadMutation,
} from '../LeadsPage/LeadFormContext';

import {LeadFormValues} from './addAccountFields';
import {addAccountStep, AddAccountStepper} from './AddAccountStepper';
import {useDjangoContext} from '../../contexts/useAuthContext';
import {CreateContact, CreateContactVariables} from '../../schema-types/CreateContact';
import {
  AddSecondaryContact,
  AddSecondaryContactVariables,
} from '../../schema-types/AddSecondaryContact';
import {AccountDetailsFragment} from '../../schema-types/AccountDetailsFragment';

import {
  SubmitPrimaryAccount,
  SubmitPrimaryAccountVariables,
} from '../../schema-types/SubmitPrimaryAccount';
import {AccountFull, AccountFullVariables} from '../../schema-types/AccountFull';
import validateUUID from '../../utils/validateUUID';
import {useRequirementsDentistOrBAU} from '../../hooks/useRequirementsDentistOrBAU';
// import {useCreateAccountFirstReview} from './AddAccountSetupAccount';

export const AddAccountContext = React.createContext<{
  account: AccountDetailsFragment | null;
  goToStep: (step: addAccountStep) => void;
  currentStep: addAccountStep;
  // onCreateOrUpdate: (updateVars: LeadFormValues) => Promise<string>;
  onCreateOrUpdateLoading: boolean;
  saving: boolean;
  setAccountId: (id: string) => void;
}>({
  account: null,
  setAccountId: () => {},
  goToStep: () => {},
  currentStep: 'basic',
  // onCreateOrUpdate: async () => '',
  onCreateOrUpdateLoading: false,
  saving: false,
});

export const useAddAccountContext = () => {
  const context = useContext(AddAccountContext);
  return context;
};

const LeadFormDiv: React.FC<PropsWithChildren> = ({children}) => {
  return <>{children}</>;
};

const convertLeadMutation = gql`
  mutation ConvertLead($leadId: UUID!) {
    convertLead(leadId: $leadId, isSppDa: true) {
      ok
      errors
      contact {
        id
      }
      account {
        id
        curoId
      }
    }
  }
`;

const submitContactMutation = gql`
  mutation SubmitNewContact($contactId: UUID!) {
    submitContact(contactId: $contactId) {
      ok
      contact {
        id
      }
    }
  }
`;

const createContactMutation = gql`
  mutation CreateContact(
    $firstName: String!
    $middleName: String
    $lastName: String!
    $ageVulnerability: Boolean
    $anniversary: DateTime
    $bereavementVulnerability: Boolean
    $divorceVulnerability: Boolean
    $dob: DateTime
    $email: String
    $familyStatus: String
    $financialDifficultiesVulnerability: Boolean
    $gender: String
    $genderReassignment: Boolean
    $healthVulnerability: Boolean
    $hearingVulnerability: Boolean
    $inexperienceVulnerability: Boolean
    $informalSalutation: String
    $isDeleted: Boolean
    $jobTitle: String
    $languageVulnerability: Boolean
    $learningVulnerability: Boolean
    $mentalCapacityVulnerability: Boolean
    $mentalHealthVulnerability: Boolean
    $name: String
    $nationality: String
    $otherEventVulnerability: Boolean
    $otherVulnerability: Boolean
    $primaryPhone: String
    $questionnaireStatus: String
    $specialCareApplies: String
    $specialCareReasons: String
    # $owner: String
    $knownAs: String
    $spouseName: String
    $visionVulnerability: Boolean
    $nationalInsurance: String
    $residency: String
    $domicile: String
    $isSppDa: Boolean!
    $title: String
    $addressId: UUID
  ) {
    createContact(
      firstName: $firstName
      middleName: $middleName
      lastName: $lastName
      knownAs: $knownAs
      ageVulnerability: $ageVulnerability
      anniversary: $anniversary
      bereavementVulnerability: $bereavementVulnerability
      isSppDa: $isSppDa
      divorceVulnerability: $divorceVulnerability
      dob: $dob
      email: $email
      familyStatus: $familyStatus
      financialDifficultiesVulnerability: $financialDifficultiesVulnerability
      gender: $gender
      genderReassignment: $genderReassignment
      healthVulnerability: $healthVulnerability
      hearingVulnerability: $hearingVulnerability
      inexperienceVulnerability: $inexperienceVulnerability
      informalSalutation: $informalSalutation
      isDeleted: $isDeleted
      jobTitle: $jobTitle
      languageVulnerability: $languageVulnerability
      learningVulnerability: $learningVulnerability
      mentalCapacityVulnerability: $mentalCapacityVulnerability
      title: $title
      mentalHealthVulnerability: $mentalHealthVulnerability
      name: $name
      nationality: $nationality
      nationalInsurance: $nationalInsurance
      residency: $residency
      domicile: $domicile
      # owner: $owner
      otherEventVulnerability: $otherEventVulnerability
      otherVulnerability: $otherVulnerability
      primaryPhone: $primaryPhone
      questionnaireStatus: $questionnaireStatus
      specialCareApplies: $specialCareApplies
      specialCareReasons: $specialCareReasons
      spouseName: $spouseName
      visionVulnerability: $visionVulnerability
      addressId: $addressId
    ) {
      contact {
        ...ContactSearchFragment
      }
    }
  }
  ${CONTACT_SEARCH_FRAGMENT}
`;

const addSecondaryContactMutation = gql`
  mutation AddSecondaryContact($accountId: UUID!, $contactId: UUID!) {
    createAccountContact(
      accountContactRole: secondary
      accountId: $accountId
      contactId: $contactId
    ) {
      ok
    }
  }
`;

const submitAccountMutation = gql`
  mutation SubmitPrimaryAccount($accountId: UUID!) {
    submitAccount(accountId: $accountId) {
      ok
      errors
      account {
        ...AccountDetailsFragment
      }
    }
  }
  ${ACCOUNT_DETAILS_FRAGMENT}
`;

const AddLeadFormWrapped = withAutoFormNew<LeadFormValues, PropsWithChildren>(
  LeadFormDiv,
  undefined,
  {
    debugPosition: 'right',
  }
);

export const AddAccountContextProvider: React.FC<PropsWithChildren> = ({children}) => {
  const [step, setStep] = useState<addAccountStep>('basic');
  const {recommendsGridUrl} = useRequirementsDentistOrBAU();
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();
  const [accountId, setAccountId] = useState<null | string>(null);
  // const {createFirstReview, createFirstReviewLoading, done, error} =
  //   useCreateAccountFirstReview();
  // const [contactId, setContactId] = useState<null | string>(null);
  const [leadId, setLeadId] = useState<null | string>(null);
  const {
    loading: accountLoading,
    error: accountError,
    data: account,
  } = useQuery<AccountFull, AccountFullVariables>(GET_ACCOUNT_FULL, {
    variables: {
      accountId: validateUUID(accountId)!,
    },
    skip: !accountId,
  });
  const [onCreateAddressMutation, {error: createError}] = useMutation<
    CreateLeadAddress,
    CreateLeadAddressVariables
  >(createLeadAddressMutation);
  const [onUpdateAddressMutation, {error: updateError}] = useMutation<
    UpdateLeadAddress,
    UpdateLeadAddressVariables
  >(updateLeadAddressMutation);
  const [onCreateLeadMutation, {error: createLeadError}] = useMutation<
    CreateLead,
    CreateLeadVariables
  >(createLeadMutation);
  const [onUpdateLeadMutation, {error: updateLeadError}] = useMutation<
    UpdateLead,
    UpdateLeadVariables
  >(updateLeadMutation);
  const [onConvertLead, {error: convertLeadToAccountError}] = useMutation<
    ConvertLead,
    ConvertLeadVariables
  >(convertLeadMutation);
  const [onSubmitAccount, {error: SubmitAccountError}] = useMutation<
    SubmitPrimaryAccount,
    SubmitPrimaryAccountVariables
  >(submitAccountMutation);
  const [onSubmitContact, {error: SubmitContactError}] = useMutation<
    SubmitNewContact,
    SubmitNewContactVariables
  >(submitContactMutation);
  const [onCreateContact, {error: createContactError}] = useMutation<
    CreateContact,
    CreateContactVariables
  >(createContactMutation);
  const [onAddSecondaryContactToAccount, {error: updateAccountErro}] = useMutation<
    AddSecondaryContact,
    AddSecondaryContactVariables
  >(addSecondaryContactMutation);

  const onCreateOrUpdate = useCallback(
    async function onCreateOrUpdate(updateVars: LeadFormValues): Promise<string> {
      setSaving(true);
      enqueueSnackbar('Creating client. This may take 2-3 minutes.', {
        variant: 'info',
        persist: true,
        action: () => (
          <Button
            type="button"
            onClick={() => {
              closeSnackbar();
              setSaving(false);
            }}
          >
            Cancel
          </Button>
        ),
      });
      try {
        let newOrExistingLeadId = leadId;
        if (!leadId) {
          const {data: createData} = await onCreateLeadMutation({
            variables: {
              firstName: updateVars.primary.firstName,
              lastName: updateVars.primary.lastName,
              middleName: updateVars.primary.middleName || '',
            },
          });
          if (createData?.createLead?.lead?.id) {
            newOrExistingLeadId = createData?.createLead?.lead.id;
            setLeadId(newOrExistingLeadId);
          }
        }
        if (!newOrExistingLeadId) {
          throw Error('Unable to initiate client creation');
        }
        // } else {
        //   newOrExistingLeadId = leadId;
        // }

        const variables: UpdateLeadVariables = {
          nationality: updateVars.primary.nationality,
          knownAs: updateVars.primary.knownAs,
          leadId: newOrExistingLeadId,
          accountId: validateUUID(updateVars.accountId),
          accountName: updateVars.accountName,
          accountType: updateVars.accountType,
          adviserId: updateVars.adviser,
          middleName: updateVars.primary.middleName,
          companyName: updateVars.companyName,
          contactId: updateVars.primary.contactId
            ? validateUUID(updateVars.primary.contactId)
            : undefined,
          dob: updateVars.primary.dob,
          firstName: updateVars.primary.firstName,
          homePhone: updateVars.primary.homePhone,
          jobTitle: updateVars.primary.jobTitle,
          lastName: updateVars.primary.lastName,
          // leadSource: updateVars.primary.leadSource,
          linkedinProfile: updateVars.primary.linkedinProfile,
          mobilePhone: updateVars.primary.mobilePhone,
          nationalInsurance: updateVars.primary.nationalInsurance,
          primaryEmail: updateVars.primary.primaryEmail,
          domicile: updateVars.primary.taxDomicile,
          residency: updateVars.primary.taxResidency,
          // relatedLeadId: updateVars.primary.relatedLeadId,
          secondaryEmail: updateVars.primary.secondaryEmail,
          title: updateVars.primary.title,
          initialFixedFee:
            // eslint-disable-next-line no-nested-ternary
            updateVars.initialOrFixedFeeField === 'initialFixedFee'
              ? updateVars.initialFixedFee
              : updateVars.initialOrFixedFeeField === 'initialNoFee'
              ? 0
              : undefined,
          initialPercentageFee:
            updateVars.initialOrFixedFeeField === 'initialPercentageFee'
              ? updateVars.initialPercentageFee
              : undefined,
          ongoingFee: updateVars.ongoingFee,
        };

        // eslint-disable-next-line consistent-return
        const {data: updateData} = await onUpdateLeadMutation({
          variables,
        });
        let leadAddressId = null;
        if (
          updateData?.updateLead?.lead?.addresses.length &&
          updateData?.updateLead?.lead?.addresses[0].id
        ) {
          // if an address, use that
          leadAddressId = updateData?.updateLead?.lead?.addresses[0].id;
        } else {
          // if no address, then create an address
          const {data: createLeadData} = await onCreateAddressMutation({
            variables: {
              leadId: newOrExistingLeadId,
              isPrimary: true,
            },
          });
          if (!createLeadData?.createAddress?.address?.id) {
            throw Error('Unable to create client address');
          }
          leadAddressId = createLeadData?.createAddress?.address.id;
        }
        await onUpdateAddressMutation({
          variables: {
            leadId: newOrExistingLeadId,
            addressId: leadAddressId,
            isPrimary: true,
            city: updateVars.city,
            line1: updateVars.line1,
            line2: updateVars.line2,
            line3: updateVars.line3,
            country: updateVars.country,
            county: updateVars.county,
            postcode: updateVars.postcode,
          },
        });

        const {data: convertLeadToAccountData} = await onConvertLead({
          variables: {
            leadId: newOrExistingLeadId,
          },
        });
        if (!convertLeadToAccountData?.convertLead?.contact?.id) {
          throw Error('Unable to create contact');
        }
        if (!convertLeadToAccountData?.convertLead?.account?.id) {
          throw Error('Unable to convert to account');
        }
        const primaryContactId = convertLeadToAccountData.convertLead.contact.id;
        await onSubmitContact({
          variables: {
            contactId: primaryContactId,
          },
        });

        if (updateVars.singleJoint === 'joint') {
          const secondary = updateVars.secondary!;
          const {data: createSecondaryContactData} = await onCreateContact({
            variables: {
              firstName: secondary.firstName,
              lastName: secondary.lastName,
              middleName: secondary.middleName || '',
              nationality: secondary.nationality,
              knownAs: secondary.knownAs,
              dob: secondary.dob,
              domicile: secondary.taxDomicile,
              residency: secondary.taxResidency,
              primaryPhone: secondary.mobilePhone || secondary.homePhone,
              nationalInsurance: secondary.nationalInsurance,
              email: secondary.primaryEmail,
              isSppDa: true,
              title: secondary.title,
              addressId: leadAddressId,
            },
          });
          if (!createSecondaryContactData?.createContact?.contact?.id) {
            throw Error('Unable to create secondary contact');
          }
          const secondaryContactId = createSecondaryContactData.createContact.contact.id;
          await onAddSecondaryContactToAccount({
            variables: {
              accountId: convertLeadToAccountData.convertLead.account.id,
              contactId: secondaryContactId,
            },
          });
          await onSubmitContact({
            variables: {
              contactId: secondaryContactId,
            },
          });
        }

        // await onUpdateAccount({
        //   variables: {
        //     accountId: convertLeadToAccountData.convertLead.account.id,
        //     // propositionType: 'SPP',
        //     clientType: 'Client',
        //   },
        // });

        const {data: submitAccountData} = await onSubmitAccount({
          variables: {
            accountId: convertLeadToAccountData.convertLead.account.id,
          },
        });

        const submitErrors = submitAccountData?.submitAccount?.errors ?? [];
        if (submitErrors.length) {
          throw Error(submitErrors.filter(Boolean).join('. '));
        }

        if (submitAccountData?.submitAccount?.account?.id) {
          closeSnackbar();
          enqueueSnackbar('Client successfully created!', {
            variant: 'success',
          });
          setAccountId(convertLeadToAccountData.convertLead.account.id);
          setSaving(false);
          if (updateVars.primary.firstName.toLocaleLowerCase().includes('test')) {
            // skip straight to requirements if testing
            navigate(
              `/account/${submitAccountData?.submitAccount?.account?.id}/${recommendsGridUrl}`,
              {}
            );
          } else {
            navigate(
              `/add-account/${submitAccountData?.submitAccount?.account?.id}/setup-account`
            );
          }
          return submitAccountData.submitAccount.account.id;
        }
        throw Error('Unable to submit client to curo');
      } catch (err: any) {
        setSaving(false);
        closeSnackbar();
        enqueueSnackbar(
          <>
            <Typography variant="h4">
              Unable to create client, please try again: ${err.message ?? ''}
            </Typography>
            <Typography>{err.message}</Typography>
          </>,
          {
            variant: 'error',
            autoHideDuration: null,
          }
        );
        captureException(err);
      }
      return '';
    },
    [onCreateLeadMutation, onUpdateLeadMutation]
  );

  const contextValue = useMemo(() => {
    return {
      // account: data?.leadDetails || null,
      account: account?.accountDetails || null,
      currentStep: step,
      goToStep: setStep,
      // onCreateOrUpdate: async (vars) => {
      //   const result = await onCreateOrUpdate(vars);
      //   setSaving(false);
      //   return result;
      // },
      setAccountId,
      onCreateOrUpdateLoading: false,

      saving,
    };
  }, [
    // account: data?.leadDetails || null,
    account,
    step,
    setStep,
    setAccountId,
    saving,
  ]);

  // const onSubmitLead = useCallback(async function onSubmitLead(
  //   updateVars: LeadFormValues
  // ): Promise<any> {
  //   try {
  //     setSaving(true);
  //     const id = await onCreateOrUpdate(updateVars);
  //     if (id) {
  //       const {data: submitData} = await onSubmitLeadMutation({
  //         variables: {
  //           leadId: id,
  //         },
  //       });

  //       if (submitData?.submitLead?.ok) {
  //         navigate(`/add-account/setup-account`);
  //         return;
  //       }
  //       enqueueSnackbar('Unable to submit lead', {variant: 'error'});
  //     }
  //   } finally {
  //     setSaving(false);
  //   }
  // },
  // []);

  // const initialValueOverrides: Partial<LeadFormValues> = useMemo(() => {
  //   const lead = data?.leadDetails;
  //   const firstAddress = lead?.addresses.length ? lead.addresses[0] : null;
  //   const returnVal: Partial<LeadFormValues> = {
  //     accountId: lead?.account?.id || undefined,
  //     accountName: lead?.accountName || undefined,
  //     accountType: lead?.accountType || undefined,

  //     companyName: lead?.companyName || undefined,

  //     // dob: lead?.dob || undefined,
  //     // firstName: lead?.firstName || undefined,
  //     // lastName: lead?.lastName || undefined,
  //     // // leadSource: lead?.leadSource || undefined,
  //     // jobTitle: lead?.jobTitle || undefined,
  //     // // adviserId: lead?.adviserId || undefined,
  //     // homePhone: lead?.homePhone || undefined,
  //     // mobilePhone: lead?.mobilePhone || undefined,
  //     // leadId: lead?.id || undefined,
  //     // linkedinProfile: lead?.linkedinProfile || undefined,
  //     // nationalInsurance: lead?.nationalInsurance || undefined,
  //     // primaryEmail: lead?.primaryEmail || undefined,
  //     // secondaryEmail: lead?.secondaryEmail || undefined,
  //     // title: lead?.title || undefined,
  //     primary: {
  //       dob: lead?.dob || undefined,
  //       firstName: lead?.firstName || '',
  //       lastName: lead?.lastName || '',
  //       // leadSource: lead?.leadSource || '',
  //       jobTitle: lead?.jobTitle || '',
  //       // adviserId: lead?.adviserId || '',
  //       homePhone: lead?.homePhone || '',
  //       mobilePhone: lead?.mobilePhone || '',
  //       contactError: '',
  //       linkedinProfile: lead?.linkedinProfile || '',
  //       nationalInsurance: lead?.nationalInsurance || '',
  //       primaryEmail: lead?.primaryEmail || '',
  //       secondaryEmail: lead?.secondaryEmail || '',
  //       title: lead?.title || '',
  //       contactId: lead?.contact?.id || '',
  //     },

  //     line1: firstAddress?.line1 || '',
  //     line2: firstAddress?.line2 || '',
  //     line3: firstAddress?.line3 || '',
  //     county: firstAddress?.county || '',
  //     // moveInDate: firstAddress,
  //     postcode: firstAddress?.postcode || '',
  //     city: firstAddress?.city || '',
  //     // addresses
  //     // addresses: [
  //     //   {
  //     //     address1: '',
  //     //     address2: '',
  //     //     address3: '',
  //     //     county: '',
  //     //     moveInDate: new Date(),
  //     //     postcode: '',
  //     //     city: '',
  //     //   },
  //     // ],
  //   };
  //   return returnVal;
  // }, [data?.leadDetails]);

  const initialValueOverrides: RecursivePartial<LeadFormValues> = {
    accountType: 'Household',
    country: 'GB',
    singleJoint: 'single',

    primary: {
      nationality: 'GB',
      taxDomicile: 'GB',
      taxResidency: 'GB',
    },
    secondary: {
      nationality: 'GB',
      taxDomicile: 'GB',
      taxResidency: 'GB',
    },
    agree: false,
  };
  return (
    <Box
      id="add-account"
      position="relative"
      // border="1px solid blue"
      maxHeight="100%"
      display="flex"
      flexDirection="column"
    >
      <AddLeadFormWrapped
        submit={(values) => {
          onCreateOrUpdate(values);
        }}
        formFields={[]}
        initialValueOverrides={initialValueOverrides}
        // initialValueOverrides={initialValueOverrides}
      >
        <AddAccountContext.Provider value={contextValue}>
          <Box
            display="flex"
            flexDirection="column"
            overflow="scroll"
            // border="1px solid red"
            maxHeight="100%"
            position="relative"
          >
            <Box>
              <AddAccountStepper currentStep={step} />
            </Box>

            <Box mb={8} flex="1 1 auto" overflow="scroll">
              {children}
            </Box>
          </Box>
        </AddAccountContext.Provider>
      </AddLeadFormWrapped>
    </Box>
  );
};
